/**
 * @healthos/api-client - the configurable fetch contract for every HealthOS
 * UI package and page.
 *
 * The host app calls `configureApiClient({ fetch, baseUrl })` once at
 * startup with whatever auth-bearing fetch implementation it owns
 * (companion-web wires Keycloak Bearer tokens via `authedFetch`; the
 * destination Thrive-HealthOS host wires its own session). Everything
 * downstream imports `{ apiFetch }` and forgets about auth.
 *
 * Keeping this contract in its own zero-dependency package means the
 * experience layer (pages + ui-plan + ui-shell) can be ported to a new
 * host without rewriting every call site - the consumer provides one
 * config, and every fetch in the tree picks it up.
 *
 * Design notes:
 *  - Singleton intentionally. Multiple clients in one app means multiple
 *    auth contexts, which we have a single source of truth for at the
 *    OIDC layer; mirroring that here keeps the seam tight.
 *  - Default impl is `globalThis.fetch` with no auth and no base URL.
 *    A misconfigured app will hit unauthenticated requests and surface
 *    401s rather than silently corrupt; that's the desired failure mode.
 *  - The contract is `(input, init) => Promise<Response>` - exactly the
 *    DOM `fetch` shape - so any auth wrapper that already conforms can
 *    be passed through as-is.
 */

/**
 * The minimum-viable fetch contract: takes the same input shape as the
 * DOM `fetch` and returns a Response. Deliberately narrower than
 * `typeof fetch` because Bun extends the global `fetch` type with a
 * `preconnect` method, which would force every host (including ours)
 * to ship a wrapper that satisfies it. The DOM Response surface is
 * what every consumer actually needs.
 */
export type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface ApiClientConfig {
  /**
   * Fetch implementation. Default: `globalThis.fetch` (unauthenticated).
   * The host typically passes a wrapper that attaches a Bearer token
   * from the active OIDC session.
   */
  fetch: ApiFetch;
  /**
   * Optional base URL prepended to relative paths. Default: '' (same
   * origin). Useful when the SPA and the API live on different origins
   * (e.g. SPA on the CDN, API on a worker subdomain).
   */
  baseUrl?: string;
}

const DEFAULT_CONFIG: ApiClientConfig = {
  fetch: (input, init) => globalThis.fetch(input as RequestInfo, init),
  baseUrl: "",
};

let current: ApiClientConfig = DEFAULT_CONFIG;

/**
 * Wire the host's fetch implementation. Idempotent - calling it again
 * replaces the active config (useful for hot-reload or a sign-out path
 * that wants to revert to the unauthenticated default).
 */
export function configureApiClient(config: Partial<ApiClientConfig>): void {
  current = { ...DEFAULT_CONFIG, ...config };
}

/**
 * Read the active config. Mostly useful for diagnostics and tests; in
 * normal code prefer calling `apiFetch` directly.
 */
export function getApiClient(): ApiClientConfig {
  return current;
}

/**
 * Reset back to the unauthenticated default. Test helper, also handy
 * after a forced sign-out so a stale token isn't sent on the next
 * request.
 */
export function resetApiClient(): void {
  current = DEFAULT_CONFIG;
}

/**
 * The universal fetch wrapper. Pages and packages call this instead of
 * `globalThis.fetch` so the host can decide auth + base URL once.
 *
 * Path is treated as relative; absolute URLs (http:// / https://) bypass
 * `baseUrl` so callers can still hit external endpoints when needed
 * without unconfiguring the client.
 */
export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const { fetch: f, baseUrl } = current;
  const isAbsolute = /^[a-z][a-z\d+\-.]*:\/\//i.test(path);
  const url = !isAbsolute && baseUrl ? `${baseUrl.replace(/\/$/, "")}${path}` : path;
  return f(url, init);
}
